import { useState, useEffect } from 'react'
import {
    Box, Grid, Paper, Typography, Button, Toolbar, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Switch, FormControlLabel, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, DialogContentText, TextField, Select,
    MenuItem, FormControl, InputLabel, Tooltip
} from '@mui/material'
import {
    ArrowBackIos, Refresh, MenuBook, PictureAsPdf, Edit, Delete,
    Add, Visibility, Close
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useStatus } from '../../context/Status.context'
import { useUsersContext } from '../../context'
import { manualsRoutes } from '../../routes'
import { GenerationStatusModal } from '../../dialogs/StatusModal'

const AdminManualsPage = () => {
    const navigate = useNavigate()
    const { setstatus } = useStatus()
    const { roles } = useUsersContext()

    const [manuals, setManuals] = useState([])
    const [loading, setLoading] = useState(false)

    // PDF Viewer State (Right Column)
    const [selectedViewerManual, setSelectedViewerManual] = useState(null)
    const [viewerUrl, setViewerUrl] = useState('')
    const [loadingViewer, setLoadingViewer] = useState(false)

    // Form / Dialog Modal States (Creation & Edition)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editingManualId, setEditingManualId] = useState(null)

    const [formTitle, setFormTitle] = useState('')
    const [formDescription, setFormDescription] = useState('')
    const [formFile, setFormFile] = useState(null)
    const [formRoles, setFormRoles] = useState([])
    const [submitting, setSubmitting] = useState(false)

    // Overwrite Confirmation Modal State
    const [overwriteModalOpen, setOverwriteModalOpen] = useState(false)
    const [conflictMessage, setConflictMessage] = useState('')

    const fetchAdminManuals = async () => {
        setLoading(true)
        try {
            const res = await manualsRoutes.getAdminManuals()
            if (res && res.data) {
                setManuals(res.data)
            }
        } catch (err) {
            console.error('Error fetching admin manuals:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAdminManuals()
    }, [])

    // Open Modal for Create
    const handleOpenCreateModal = () => {
        setIsEditing(false)
        setEditingManualId(null)
        setFormTitle('')
        setFormDescription('')
        setFormFile(null)
        setFormRoles([])
        setDialogOpen(true)
    }

    // Open Modal for Edit
    const handleOpenEditModal = (manual) => {
        setIsEditing(true)
        setEditingManualId(manual._id)
        setFormTitle(manual.title || '')
        setFormDescription(manual.description || '')
        setFormRoles((manual.rolesAllowed || []).map(r => r._id || r))
        setFormFile(null)
        setDialogOpen(true)
    }

    // Toggle Active / Inactive State
    const handleToggleState = async (manualId, currentState) => {
        try {
            await manualsRoutes.toggleManualState(manualId, !currentState)
            await fetchAdminManuals()
        } catch (err) {
            console.error('Error toggling state:', err)
            setstatus({
                status: 'error',
                title: 'Error de Estado',
                message: 'No se pudo cambiar el estado del manual.',
                severity: 'error'
            })
        }
    }

    // Delete Manual (Soft Delete)
    const handleDeleteManual = async (manual) => {
        if (!window.confirm(`¿Está seguro que desea eliminar el manual "${manual.title}"?`)) {
            return
        }

        setstatus({
            status: 'loading',
            title: 'Eliminando Manual',
            message: 'Procesando eliminación del documento...'
        })

        try {
            await manualsRoutes.deleteManual(manual._id)
            setstatus({
                status: 'completed',
                title: 'Manual Eliminado',
                message: `El manual "${manual.title}" fue eliminado exitosamente.`,
                severity: 'success'
            })
            if (selectedViewerManual && selectedViewerManual._id === manual._id) {
                setSelectedViewerManual(null)
                setViewerUrl('')
            }
            await fetchAdminManuals()
        } catch (err) {
            console.error('Error deleting manual:', err)
            setstatus({
                status: 'error',
                title: 'Error al Eliminar',
                message: 'No se pudo eliminar el manual.',
                severity: 'error'
            })
        }
    }

    // View Manual PDF in Right Column
    const handleViewManual = async (manual) => {
        setSelectedViewerManual(manual)
        setLoadingViewer(true)
        setViewerUrl('')

        try {
            const res = await manualsRoutes.getManualDownloadUrl(manual._id)
            if (res && res.data && res.data.url) {
                setViewerUrl(res.data.url)
            }
        } catch (err) {
            console.error('Error loading manual URL:', err)
            setstatus({
                status: 'error',
                title: 'Error al Visualizar',
                message: 'No se pudo generar el enlace de visualización para el manual.',
                severity: 'error'
            })
        } finally {
            setLoadingViewer(false)
        }
    }

    // Submit Create or Edit Form
    const handleSubmitForm = async (e, forceOverwrite = false) => {
        if (e && e.preventDefault) e.preventDefault()

        if (!isEditing && !formFile) {
            setstatus({
                status: 'error',
                title: 'Archivo requerido',
                message: 'Por favor seleccione un archivo PDF para subir.',
                severity: 'warning'
            })
            return
        }

        const maxSizeBytes = 15 * 1024 * 1024 // 15MB
        if (formFile && formFile.size > maxSizeBytes) {
            setstatus({
                status: 'error',
                title: 'Tamaño excedido',
                message: 'El archivo seleccionado supera el tamaño máximo permitido de 15 MB.',
                severity: 'error'
            })
            return
        }

        if (!formTitle.trim() || !formDescription.trim()) {
            setstatus({
                status: 'error',
                title: 'Campos requeridos',
                message: 'Debe ingresar un título y una descripción para el manual.',
                severity: 'warning'
            })
            return
        }

        setSubmitting(true)
        setstatus({
            status: 'loading',
            title: isEditing ? 'Guardando Cambios' : 'Subiendo Manual',
            message: 'Almacenando archivo en servidor Azure... Por favor espere.'
        })

        try {
            const formData = new FormData()
            formData.append('title', formTitle.trim())
            formData.append('description', formDescription.trim())
            formData.append('rolesAllowed', JSON.stringify(formRoles))
            if (formFile) {
                formData.append('file', formFile)
            }
            if (forceOverwrite) {
                formData.append('overwrite', 'true')
            }

            if (isEditing) {
                await manualsRoutes.updateManual(editingManualId, formData)
            } else {
                await manualsRoutes.uploadManual(formData)
            }

            setstatus({
                status: 'completed',
                title: isEditing ? 'Manual Actualizado' : 'Manual Subido Exitosamente',
                message: `El manual "${formTitle}" fue guardado correctamente.`,
                severity: 'success'
            })

            setDialogOpen(false)
            setOverwriteModalOpen(false)
            await fetchAdminManuals()
        } catch (error) {
            console.error('Error submitting form:', error)
            const httpStatus = error.response?.status
            if (httpStatus === 409 && !isEditing) {
                const msg = error.response?.data?.message || 'Ya existe un manual almacenado con ese mismo nombre de archivo.'
                setConflictMessage(msg)
                setOverwriteModalOpen(true)
                setstatus({
                    status: 'error',
                    title: 'Documento Existente',
                    message: 'Ya existe un manual con ese nombre de archivo. Confirme si desea sobrescribirlo.',
                    severity: 'warning'
                })
            } else {
                const serverMessage = error.response?.data?.message
                const errorMsg = Array.isArray(serverMessage)
                    ? serverMessage.join(', ')
                    : (typeof serverMessage === 'string' ? serverMessage : (error.message || 'Error en la operación.'))
                setstatus({
                    status: 'error',
                    title: 'Error al Guardar',
                    message: 'No se pudo guardar el manual.',
                    errorDetails: {
                        code: httpStatus || '400',
                        info: errorMsg
                    },
                    severity: 'error'
                })
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Box sx={{ pt: 12, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            {/* TOOLBAR SUPERIOR CON BOTÓN "SUBIR MANUAL" */}
            <Paper elevation={0} sx={{ p: 1.5, mb: 3, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIos sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Administración - Gestión de Manuales de Uso
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button startIcon={<Refresh />} onClick={fetchAdminManuals} disabled={loading} variant="outlined" size="small">
                        Refrescar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleOpenCreateModal}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Subir Manual
                    </Button>
                </Box>
            </Paper>

            {/* SECCIÓN PRINCIPAL: 2 COLUMNAS (1 COL INFORMACIÓN/ACCIONES + 1 COL VISUALIZADOR PDF) */}
            <Grid container spacing={3}>
                {/* COLUMNA 1 (IZQUIERDA): TABLA DE INFORMACIÓN Y ACCIONES */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, minHeight: 650, backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Manuales Registrados ({manuals.length})
                            </Typography>
                            <Chip label="PDF Máx 15MB" color="info" size="small" variant="outlined" />
                        </Box>

                        {loading ? (
                            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={30} /></Box>
                        ) : manuals.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 4 }}>
                                No hay manuales de uso subidos. Haga clic en "Subir Manual" para agregar uno.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ maxHeight: 580 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Título / Descripción</strong></TableCell>
                                            <TableCell><strong>Roles</strong></TableCell>
                                            <TableCell align="center"><strong>Estado</strong></TableCell>
                                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {manuals.map((m) => {
                                            const isSelected = selectedViewerManual && selectedViewerManual._id === m._id
                                            return (
                                                <TableRow key={m._id} selected={isSelected}>
                                                    <TableCell>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{m.title}</Typography>
                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{m.description}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {m.rolesAllowed && m.rolesAllowed.length > 0 ? (
                                                                m.rolesAllowed.map((r) => (
                                                                    <Chip key={r._id || r} label={r.name || r} size="small" variant="outlined" />
                                                                ))
                                                            ) : (
                                                                <Chip label="Todos" size="small" color="default" />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={!!m.state}
                                                                    onChange={() => handleToggleState(m._id, m.state)}
                                                                    size="small"
                                                                    color="success"
                                                                />
                                                            }
                                                            label={m.state ? 'Activo' : 'Inactivo'}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                            <Tooltip title="Ver Manual PDF">
                                                                <IconButton
                                                                    size="small"
                                                                    color={isSelected ? 'secondary' : 'info'}
                                                                    onClick={() => handleViewManual(m)}
                                                                >
                                                                    <Visibility fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Editar Manual">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleOpenEditModal(m)}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Eliminar Manual">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteManual(m)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>

                {/* COLUMNA 2 (DERECHA): VISUALIZADOR DE PDF */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, minHeight: 650, display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MenuBook color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {selectedViewerManual ? `Visualizando: ${selectedViewerManual.title}` : 'Visualizador de Documento PDF'}
                                </Typography>
                            </Box>
                            {selectedViewerManual && (
                                <IconButton size="small" onClick={() => { setSelectedViewerManual(null); setViewerUrl('') }}>
                                    <Close fontSize="small" />
                                </IconButton>
                            )}
                        </Box>

                        {loadingViewer ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, py: 10 }}>
                                <CircularProgress size={40} sx={{ mb: 2 }} />
                                <Typography variant="body2" color="textSecondary">Generando vista previa segura del manual...</Typography>
                            </Box>
                        ) : viewerUrl ? (
                            <Box sx={{ flexGrow: 1, width: '100%', minHeight: 580, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                <iframe
                                    src={viewerUrl}
                                    title={selectedViewerManual?.title || 'Visualizador PDF'}
                                    width="100%"
                                    height="580px"
                                    style={{ border: 'none' }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, border: '2px dashed #e0e0e0', borderRadius: 3, p: 4, textAlign: 'center' }}>
                                <PictureAsPdf sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#666' }}>
                                    Vista Previa de Manual
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, maxWidth: 320 }}>
                                    Seleccione el botón de ojo <strong>"Ver Manual"</strong> en la lista de la izquierda para visualizar el documento PDF interactivo.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* DIÁLOGO UNIFICADO DE CREACIÓN / EDICIÓN DE MANUAL */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {isEditing ? 'Editar Manual de Uso' : 'Subir Nuevo Manual (PDF)'}
                </DialogTitle>
                <form onSubmit={handleSubmitForm}>
                    <DialogContent dividers>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                {isEditing ? 'Reemplazar Archivo PDF (Opcional - Máx 15 MB)' : 'Documento PDF (Máximo 15 MB) *'}
                            </Typography>
                            <input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setFormFile(e.target.files[0] || null)}
                                style={{ width: '100%' }}
                            />
                            {formFile && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Archivo seleccionado: {formFile.name} ({(formFile.size / (1024 * 1024)).toFixed(2)} MB)
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            fullWidth
                            size="small"
                            label="Título del Documento *"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            label="Descripción *"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="roles-allowed-select-label">Roles Autorizados</InputLabel>
                            <Select
                                labelId="roles-allowed-select-label"
                                multiple
                                value={formRoles}
                                onChange={(e) => setFormRoles(e.target.value)}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const roleObj = roles.find((r) => r._id === value)
                                            return <Chip key={value} label={roleObj ? roleObj.name : value} size="small" />
                                        })}
                                    </Box>
                                )}
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role._id} value={role._id}>
                                        {role.name} ({role.dbName})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setDialogOpen(false)} color="inherit">
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isEditing ? 'Guardar Cambios' : 'Subir Manual'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* MODAL DE CONFIRMACIÓN PARA SOBREESCRIBIR ARCHIVO DUPLICADO */}
            <Dialog open={overwriteModalOpen} onClose={() => setOverwriteModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    Documento Ya Existente
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#333' }}>
                        {conflictMessage || 'Ya existe un manual almacenado con ese mismo nombre de archivo. ¿Desea sobrescribirlo con la nueva versión?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setOverwriteModalOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={() => handleSubmitForm(null, true)} variant="contained" color="error">
                        Sobrescribir / Subir
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerationStatusModal />
        </Box>
    )
}

export default AdminManualsPage
